import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./RandomListGenerator.module.scss";
import { getSeededRandomNumber } from "../../utils/randomUtils";

const RandomListGenerator = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("single"); // "single" nebo "list"
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [removeAfterDraw, setRemoveAfterDraw] = useState(true);

  // Stav závodu
  const [raceParticipants, setRaceParticipants] = useState([]);
  const [raceProgress, setRaceProgress] = useState([]);
  const [isRacing, setIsRacing] = useState(false);

  // Stav pro režim "single"
  const [step, setStep] = useState(0);
  const [chosenIDs, setChosenIDs] = useState([]);
  const [excludedIDs, setExcludedIDs] = useState([]);
  const [markedExcludedIDs, setMarkedExcludedIDs] = useState([]);
  const [exploded, setExploded] = useState([]);
  const [winner, setWinner] = useState(null);
  const explosionTimeoutsRef = useRef([]);

  // Stav pro režim "list"
  const [finishOrder, setFinishOrder] = useState([]);

  const resetAll = () => {
    setRaceParticipants([]);
    setRaceProgress([]);
    setIsRacing(false);
    setStep(0);
    setChosenIDs([]);
    setExcludedIDs([]);
    setMarkedExcludedIDs([]);
    setExploded([]);
    setWinner(null);
    setFinishOrder([]);
    explosionTimeoutsRef.current.forEach((t) => clearTimeout(t));
    explosionTimeoutsRef.current = [];
  };

  const handleModeChange = (e) => {
    setMode(e.target.value);
    resetAll();
  };

  const addManualItem = () => {
    if (newItem.trim() !== "") {
      const obj = { id: Date.now() + Math.random(), text: newItem.trim() };
      setItems((prev) => [...prev, obj]);
      setNewItem("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addManualItem();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text
        .split(/\r?\n|,/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const newObjs = lines.map((line) => ({
        id: Date.now() + Math.random(),
        text: line,
      }));
      setItems((prev) => [...prev, ...newObjs]);
    };
    reader.readAsText(file);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleGenerate = async () => {
    if (items.length === 0) return;
    resetAll();
    if (mode === "single") {
      const count = items.length;
      // Když je počet položek více než 5, provedeme vyškrtávání (markování)
      if (count > 5) {
        const allIDs = items.map((it) => it.id);
        const chosen = [];
        while (chosen.length < 5) {
          const randIndex = await getSeededRandomNumber(allIDs.length);
          const candidateID = allIDs[randIndex];
          if (!chosen.includes(candidateID)) chosen.push(candidateID);
        }
        setChosenIDs(chosen);
        const excluded = allIDs.filter((id) => !chosen.includes(id));
        setExcludedIDs(excluded);
        setStep(1);
      } else {
        // Při 5 a méně položkách přeskočíme vyškrtávání a spustíme závod přímo
        const allIDs = items.map((it) => it.id);
        setChosenIDs(allIDs);
        setStep(2);
        setTimeout(() => setStep(3), 500);
      }
    } else if (mode === "list") {
      setRaceParticipants(items);
      setRaceProgress(items.map(() => 0));
      setFinishOrder([]);
      setIsRacing(true);
    }
  };

  useEffect(() => {
    if (step === 1 && excludedIDs.length > 0) {
      const startTime = Date.now();
      const intervalId = setInterval(() => {
        setMarkedExcludedIDs((prev) => {
          const unmarked = excludedIDs.filter((id) => !prev.includes(id));
          if (unmarked.length === 0) return prev;
          const randIndex = Math.floor(Math.random() * unmarked.length);
          const chosenID = unmarked[randIndex];
          return [...prev, chosenID];
        });
        if (Date.now() - startTime >= 2000) {
          clearInterval(intervalId);
          setStep(2);
          setTimeout(() => setStep(3), 500);
        }
      }, 300);
      return () => clearInterval(intervalId);
    }
  }, [step, excludedIDs]);

  useEffect(() => {
    if (step === 2) {
      const participants =
        items.length > 5 ? items.filter((it) => chosenIDs.includes(it.id)) : items;
      setRaceParticipants(participants);
      setRaceProgress(participants.map(() => 0));
      setExploded(participants.map(() => false));
    }
  }, [step, chosenIDs, items]);

  useEffect(() => {
    if (step === 3 && mode === "single") {
      setIsRacing(true);
      explosionTimeoutsRef.current = [];
      if (raceParticipants.length > 1) {
        const maxExplosions = Math.min(2, raceParticipants.length - 1);
        const numExplosions = Math.floor(Math.random() * maxExplosions) + 1;
        const indices = [...Array(raceParticipants.length).keys()];
        const explosionIndices = [];
        while (explosionIndices.length < numExplosions) {
          const rand = Math.floor(Math.random() * indices.length);
          const candidate = indices[rand];
          if (!explosionIndices.includes(candidate)) explosionIndices.push(candidate);
        }
        explosionIndices.forEach((idx) => {
          const delay = 1000 + Math.random() * 2000;
          const t = setTimeout(() => {
            if (!isRacing) return;
            setExploded((prev) => {
              const newExp = [...prev];
              newExp[idx] = true;
              return newExp;
            });
            setRaceProgress((prev) => {
              const newProg = [...prev];
              if (newProg[idx] > 95) newProg[idx] = 95;
              return newProg;
            });
          }, delay);
          explosionTimeoutsRef.current.push(t);
        });
      }
    }
  }, [step, raceParticipants, isRacing, mode]);

  useEffect(() => {
    let intervalId;
    if (isRacing && raceParticipants.length > 0) {
      intervalId = setInterval(() => {
        setRaceProgress((prev) =>
          prev.map((val, idx) => {
            if (mode === "single" && exploded[idx]) return val;
            if (mode === "list" && finishOrder.find((p) => p.id === raceParticipants[idx].id)) {
              return val;
            }
            let inc = Math.floor(Math.random() * 5);
            let newVal = val + inc;
            if (newVal > 100) newVal = 100;
            return newVal;
          })
        );
      }, 50);
    }
    return () => clearInterval(intervalId);
  }, [isRacing, raceParticipants, exploded, mode, finishOrder]);

  useEffect(() => {
    if (mode === "single" && isRacing) {
      const idx = raceProgress.findIndex((val, i) => val >= 100 && !exploded[i]);
      if (idx !== -1) {
        setIsRacing(false);
        setWinner(raceParticipants[idx]);
        setStep(4);
        if (removeAfterDraw && raceParticipants[idx])
          removeItem(raceParticipants[idx].id);
      }
    }
  }, [raceProgress, exploded, isRacing, raceParticipants, mode, removeAfterDraw]);

  useEffect(() => {
    if (mode === "list" && isRacing) {
      raceProgress.forEach((val, idx) => {
        const participant = raceParticipants[idx];
        if (val >= 100 && !finishOrder.find((p) => p.id === participant.id)) {
          setFinishOrder((prev) => [...prev, participant]);
        }
      });
      if (finishOrder.length === raceParticipants.length) setIsRacing(false);
    }
  }, [raceProgress, raceParticipants, finishOrder, mode, isRacing]);

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate("/")}>
        Zpět
      </button>
      <h2 className={styles.title}>Náhodný výběr z položek</h2>
      <div className={styles.modeSelector}>
        <label>
          <input
            type="radio"
            value="single"
            checked={mode === "single"}
            onChange={handleModeChange}
          />
          Vybrat jedno
        </label>
        <label>
          <input
            type="radio"
            value="list"
            checked={mode === "list"}
            onChange={handleModeChange}
          />
          Vytvořit závod
        </label>
      </div>

      {mode === "single" && (
        <div className={styles.autoRemoveToggle}>
          <label>
            <input
              type="checkbox"
              checked={removeAfterDraw}
              onChange={(e) => setRemoveAfterDraw(e.target.checked)}
            />
            Automaticky smazat vítěze
          </label>
        </div>
      )}

      <div className={styles.inputRow}>
        <input
          type="text"
          placeholder="Přidejte položku a stiskněte Enter"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
          className={styles.input}
        />
        <button onClick={addManualItem} className={styles.addButton}>
          Přidat
        </button>
        <input
          type="file"
          accept=".txt"
          onChange={handleFileUpload}
          className={styles.fileInput}
        />
      </div>

      {items.length > 0 && (
        <div className={styles.itemList}>
          <h4>Položky:</h4>
          <div className={styles.listSection}>
            {items.map((item) => (
              <div key={item.id} className={styles.item}>
                <span>{item.text}</span>
                <button
                  className={styles.deleteButton}
                  onClick={() => removeItem(item.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={handleGenerate} className={styles.generateButton}>
        Generovat
      </button>

      {/* Režim SINGLE */}
      {mode === "single" && (
        <>
          {step >= 1 && excludedIDs.length > 0 && (
            <div className={styles.allPossible}>
              <h3>Všechny možné losování:</h3>
              <ul>
                {items.map((item) => {
                  const isExcluded = excludedIDs.includes(item.id);
                  const isMarked = markedExcludedIDs.includes(item.id);
                  return (
                    <li
                      key={item.id}
                      className={isMarked ? styles.excludedItem : ""}
                    >
                      {item.text}
                      {isExcluded && isMarked && (
                        <span className={styles.crossMark}>✖</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {(step >= 2 || step === 3) && raceParticipants.length > 0 && (
            <div className={styles.raceContainer}>
              <h3>Závodníci:</h3>
              {raceParticipants.map((participant, idx) => (
                <div key={participant.id} className={styles.raceRow}>
                  <div className={styles.participantName}>{participant.text}</div>
                  <div className={styles.progressBar}>
                    <div
                      className={`
                        ${styles.progress} 
                        ${exploded[idx] ? styles.exploded : ""} 
                        ${raceProgress[idx] >= 100 && !exploded[idx] ? styles.finished : ""}
                      `}
                      style={{ width: `${raceProgress[idx] || 0}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 4 && winner && (
            <div className={styles.result}>
              <strong>Vítěz:</strong> {winner.text}
            </div>
          )}
        </>
      )}

      {/* Režim LIST (závod) */}
      {mode === "list" && (
        <>
          {raceParticipants.length > 0 && (
            <div className={styles.raceContainer}>
              <h3>Závod probíhá:</h3>
              {raceParticipants.map((participant, idx) => (
                <div key={participant.id} className={styles.raceRow}>
                  <div className={styles.participantName}>{participant.text}</div>
                  <div className={styles.progressBar}>
                    <div
                      className={`
                        ${styles.progress} 
                        ${raceProgress[idx] >= 100 ? styles.finished : ""}
                      `}
                      style={{ width: `${raceProgress[idx] || 0}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isRacing && finishOrder.length > 0 && (
            <div className={styles.resultList}>
              <h3>Pořadí závodu:</h3>
              <ol>
                {finishOrder.map((participant, index) => (
                  <p key={participant.id}>
                    {index + 1}. {participant.text}
                  </p>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RandomListGenerator;
